import {
  CLINICIAN_TYPE,
  type ClinicianType,
  type SpecialityMatchType,
} from "@src/constants/filter-constants";
import { HTTP_METHODS } from "@src/constants/http-methods.constant";
import { INTERNAL_API_ENDPOINTS } from "@src/constants/internal-api-endpoints-constants";
import type { ProviderSearchSortBy } from "@src/constants/provider-search-sort.constants";
import type { ApiCallConfig } from "@src/utils/api-client";
import { normalizeSessionTypeToken } from "@src/utils/session-type-normalization";

const DEFAULT_SPECIALITY_MATCH_TYPE: SpecialityMatchType = "or";

/** Only proximity ordering is supported in v1. */
export type ProviderSortBy = ProviderSearchSortBy;

function getStartingAgeFromLabel(label: string): number | null {
  const match = /\((\d+)(?:-\d+|\+)\)/.exec(label);
  if (!match) return null;
  return Number(match[1]);
}

function normalizeSpecialityMatchType(value?: string): SpecialityMatchType {
  return value === "and" ? "and" : DEFAULT_SPECIALITY_MATCH_TYPE;
}

export interface ProviderSearchParams {
  user_uuid?: string;
  context_id?: number;
  state?: string;
  user_latitude?: number;
  user_longitude?: number;
  packageid?: string;
  modality?: string;
  ageserved?: string | number;
  languages?: string;
  specialities?: string[];
  specialityMatchType?: SpecialityMatchType;
  therapytypes?: string;
  gender?: string;
  max_distance?: string | number | null;
  clinician_type?: ClinicianType;
  dayOfWeek?: string[];
  startHour?: number;
  endHour?: number;
  sort_by?: ProviderSortBy;
  sort_zipcode?: string;
}

function extractDistance(val: string | number | null | undefined): number | null {
  if (val == null) return null;
  if (typeof val === "number") return val;
  const s = String(val).toLowerCase();
  if (!s || s === "no limit") return null;
  const match = /(\d+)/.exec(s);
  return match ? Number.parseInt(match[1], 10) : null;
}

/**
 * Builds the callApi config for the provider-search endpoint.
 * Applies all field normalizations (modality, gender, age, therapy types, distance)
 * that were previously inline in the service.
 */
export function buildProviderSearchConfig(
  params: ProviderSearchParams,
  signal?: AbortSignal,
): ApiCallConfig {
  let numericAge: number | null;
  if (typeof params.ageserved === "number") {
    numericAge = params.ageserved;
  } else if (typeof params.ageserved === "string") {
    numericAge = getStartingAgeFromLabel(params.ageserved);
  } else {
    numericAge = null;
  }

  const formattedModality = params.modality
    ? normalizeSessionTypeToken(params.modality)
    : "no preference";

  const genderVal = params.gender?.toLowerCase();
  const genderMap: Record<string, "M" | "F"> = { male: "M", female: "F" };
  const formattedGender =
    !genderVal || genderVal === "no preference" ? undefined : genderMap[genderVal];

  const distanceValue = extractDistance(params.max_distance);

  const body: Record<string, unknown> = {
    user_uuid: params.user_uuid,
    context_id: params.context_id,
    state: params.state,
    user_latitude: params.user_latitude,
    user_longitude: params.user_longitude,
    modality: formattedModality,
    ageServed: numericAge,
    packageId: params.packageid,
    specialityMatchType: normalizeSpecialityMatchType(params.specialityMatchType),
    ...(params.clinician_type !== CLINICIAN_TYPE.PRESCRIBER && params.therapytypes
      ? { therapyTypes: params.therapytypes }
      : {}),
    ...(params.specialities && params.specialities.length > 0
      ? { specialities: params.specialities.join(",") }
      : {}),
    gender: formattedGender,
    ...(distanceValue === null ? {} : { max_distance: distanceValue }),
    ...(params.languages ? { languages: params.languages } : {}),
    ...(params.dayOfWeek && params.dayOfWeek.length > 0
      ? { dayOfWeek: params.dayOfWeek.join(",") }
      : {}),
    ...(typeof params.startHour === "number" ? { startHour: params.startHour } : {}),
    ...(typeof params.endHour === "number" ? { endHour: params.endHour } : {}),
    ...(params.sort_by ? { sort_by: params.sort_by } : {}),
    ...(params.sort_zipcode ? { sort_zipcode: params.sort_zipcode } : {}),
    clinician_type: params.clinician_type ?? "Therapist",
  };

  // Remove undefined fields
  Object.keys(body).forEach((key) => {
    if (body[key] === undefined) {
      delete body[key];
    }
  });

  return {
    method: HTTP_METHODS.POST,
    endpoint: INTERNAL_API_ENDPOINTS.PROVIDER_SEARCH,
    body,
    ...(signal ? { signal } : {}),
  };
}
